'use client'

import React, { useEffect } from 'react'
import { CornerRightUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Textarea } from '@/shared/components/ui/textarea'
import { useAutoResizeTextarea } from '@/shared/hooks/useAutoResizeTextarea'
import { cn } from '@/shared/lib/utils'

export default function ChatInput({
	disabled,
	onChange,
	value,
	onSubmit,
}: {
	disabled?: boolean
	value: string
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
	onSubmit: () => void
}) {
	const { t } = useTranslation()

	const { textareaRef, adjustHeight } = useAutoResizeTextarea({
		minHeight: 68,
		maxHeight: 200,
	})
	useEffect(() => {
		if (!disabled) {
			textareaRef.current?.focus()
		}
	}, [disabled, textareaRef])

	return (
		<div className='relative mx-auto w-full max-w-3xl'>
			<Textarea
				data-gramm='false'
				data-testid='chat-message-input'
				autoComplete='off'
				autoCorrect='off'
				placeholder={t('ai.assistant.placeholder', {
					defaultValue: 'Ask me anything!',
				})}
				className={cn(
					'w-full max-w-3xl resize-none rounded-xl bg-slate-950/5 pr-10 pl-4 text-wrap text-black ring-black/30 dark:bg-white/5 dark:text-white dark:ring-white/30',
					'min-h-[68px]'
				)}
				ref={textareaRef}
				value={value}
				onChange={(e) => {
					onChange(e)
					adjustHeight()
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && !e.shiftKey) {
						e.preventDefault()
						onSubmit()
						adjustHeight(true)
					}
				}}
				disabled={disabled}
			/>
			<button
				type='submit'
				className={cn(
					'absolute right-2 bottom-2 rounded-lg px-1 py-1',
					disabled ? 'bg-none' : 'bg-black/5 dark:bg-white/5'
				)}
			>
				{disabled ? (
					<div
						className='h-4 w-4 animate-spin rounded-sm bg-black transition duration-700 dark:bg-white'
						style={{ animationDuration: '3s' }}
					/>
				) : (
					<CornerRightUp
						className={cn(
							'h-4 w-4 transition-opacity dark:text-white',
							value ? 'opacity-100' : 'opacity-30'
						)}
					/>
				)}
			</button>
		</div>
	)
}
